import React, { useState } from 'react';
import { Button } from './ui';

export default function ExportModal({ columns, data, title, filename, onClose }) {
  const [selectedColumns, setSelectedColumns] = useState(columns.map(c => c.key));
  const [format, setFormat] = useState('xlsx');
  const [exporting, setExporting] = useState(false);

  const toggleColumn = (key) => {
    setSelectedColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleExport = async () => {
    const chosen = columns.filter(c => selectedColumns.includes(c.key));
    if (chosen.length === 0) return;
    setExporting(true);
    try {
      const { exportToCSV, exportToXLSX, exportToPDF } = await import('../utils/exportUtils');
      if (format === 'csv') {
        exportToCSV(data, chosen, filename);
      } else if (format === 'xlsx') {
        exportToXLSX(data, chosen, filename);
      } else {
        exportToPDF(data, chosen, title, filename);
      }
    } catch (e) {
      console.error('Export error:', e);
    } finally {
      setExporting(false);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Export avancé</h2>
            <p className="text-xs text-gray-500 mt-0.5">Choisissez le format et les colonnes</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" type="button" aria-label="Fermer">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Format d'export</label>
            <div className="flex gap-3">
              {[
                { value: 'xlsx', label: 'Excel (XLSX)' },
                { value: 'csv', label: 'CSV' },
                { value: 'pdf', label: 'PDF' },
              ].map(opt => (
                <label key={opt.value} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 cursor-pointer transition-colors ${format === opt.value ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                  <input type="radio" name="format" value={opt.value} checked={format === opt.value} onChange={() => setFormat(opt.value)} className="sr-only" />
                  <span className="text-sm font-medium">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Colonnes à exporter</label>
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {columns.map(col => (
                <label key={col.key} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors">
                  <input type="checkbox" checked={selectedColumns.includes(col.key)} onChange={() => toggleColumn(col.key)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span className="text-sm text-gray-700">{col.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <Button variant="secondary" size="sm" onClick={onClose}>Annuler</Button>
          <Button variant="primary" size="sm" onClick={handleExport} disabled={exporting || selectedColumns.length === 0}>
            {exporting ? 'Export en cours...' : `Exporter en ${format.toUpperCase()}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
